import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Toggle } from "@fluentui/react";
import { ISelectionDependentCommandBarItemProps } from "cayo.ui";
import { ISpnGridItem } from "@cayo/core/admin-components/grids/SpnGrid/types";
import {
  groupExpandedAndCollapsedByKey,
  groupParsedSpns,
} from "@cayo/core/admin-components/grids/SpnGrid/utils";
import useArray from "@cayo/shared/hooks/useArray";
import { commonMessages } from "@cayo/shared/messages/common";
import { spnGridMessages } from "./messages";

export const useSpnGrid = (initialItems: ISpnGridItem[] = [], readOnly?: boolean) => {
  const intl = useIntl();
  const { removeItems, array, addItems } = useArray(initialItems, "originalSPN");
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isPickerVisible, setIsPickerVisible] = useState<boolean>(false);
  const [cayoObjId, setCayoObjId] = useState<string>("");

  const collapsedKeyValues = useMemo(
    () => groupExpandedAndCollapsedByKey(array, groupParsedSpns(array)),
    [array]
  );

  const removeSelected = (selectedItems: ISpnGridItem[]) => {
    if (!selectedItems.length) {
      return;
    }
    if (isExpanded) {
      removeItems(selectedItems.map((s) => s.originalSPN));
      return;
    }
    const expandedIdsToRemove: string[] = [];
    for (const sel of selectedItems) {
      const children = collapsedKeyValues[sel.originalSPN];
      if (children) {
        for (const c of children) {
          expandedIdsToRemove.push(c.originalSPN);
        }
      }
    }
    removeItems(expandedIdsToRemove);
  };

  const addServices = (expandedItems: ISpnGridItem[]) => {
    addItems(expandedItems);
    setCayoObjId("");
  };

  const items = useMemo(() => {
    if (readOnly) {
      return [];
    }

    const expand: ISelectionDependentCommandBarItemProps = {
      key: "expand",
      text: intl.formatMessage(spnGridMessages.expand),
      onClick: () => setIsExpanded((prev) => !prev),
      onRenderIcon: () => (
        <Toggle
          checked={isExpanded}
          ariaLabel={intl.formatMessage(spnGridMessages.expand)}
          styles={{
            root: { marginBottom: 0, pointerEvents: "none" },
            pill: { pointerEvents: "none" },
          }}
        />
      ),
      buttonStyles: { label: { marginLeft: 8 } },
    };

    const addItem: ISelectionDependentCommandBarItemProps = {
      key: "add",
      text: `${intl.formatMessage(commonMessages.add)}...`,
      iconProps: { iconName: "Add" },
      onClick: () => setIsPickerVisible(() => true),
    };

    const removeItem: ISelectionDependentCommandBarItemProps = {
      key: "remove",
      text: intl.formatMessage(commonMessages.remove),
      iconProps: { iconName: "Delete" },
      dependsOnSelection: true,
      multiselect: true,
      onClickWithSelection(selectedItems?: (ISpnGridItem & { _id: number })[]) {
        removeSelected(selectedItems ?? []);
      },
    };

    return [expand, addItem, removeItem];
  }, [array, readOnly, isExpanded]);

  return {
    commands: items,
    isPickerVisible,
    hidePicker: () => setIsPickerVisible(() => false),
    items: array,
    isExpanded,
    cayoObjId,
    setCayoObjId,
    clearCayoObjId: () => setCayoObjId(""),
    addServices,
  };
};
